// ─── CHU Multi-tenant Configuration System ────────────────────────────────
// Each CHU (hospital) can have its own feature set, UI labels and AI access.
// If no CHU-specific config exists, DEFAULT_CHU_CONFIG is used as fallback.

export interface CHUUILabels {
  analysis_button: string
  analysis_button_loading: string
  exam_section_title: string
  app_name: string
  raw_image_label: string
  results_label: string
}

export interface CHUAIFeatures {
  /** Is AI interpretation enabled at all for this CHU? */
  enabled: boolean
  /**
   * Is AI locked behind a premium paywall?
   * true  → show upsell, locked overlay, no AI results
   * false → AI freely available (if enabled)
   */
  premium: boolean
  show_interpretation: boolean
  show_profile_analysis: boolean
  show_scoring: boolean
  include_ai_in_export: boolean
}

export interface CHUCollaborationFeatures {
  enabled: boolean
}

export interface CHUCommentsFeatures {
  enabled: boolean
  required_before_validation: boolean
}

export interface CHUExportFeatures {
  include_raw_images: boolean
  include_detected_bands: boolean
  include_comments: boolean
  include_operator: boolean
  include_ai_interpretation: boolean
}

export interface CHUOperatorFeatures {
  enabled: boolean
}

export interface CHUFeatures {
  ui_labels: CHUUILabels
  ai: CHUAIFeatures
  collaboration: CHUCollaborationFeatures
  comments: CHUCommentsFeatures
  export: CHUExportFeatures
  operator: CHUOperatorFeatures
}

export interface CHUConfig {
  id: string
  name: string
  features: CHUFeatures
}

// ─── Default config ────────────────────────────────────────────────────────
// AI is PREMIUM by default — users see upsell, not AI results.
// Manual validation workflow is the base product.

export const DEFAULT_CHU_CONFIG: CHUConfig = {
  id: 'default',
  name: 'Careflai',
  features: {
    ui_labels: {
      analysis_button: "Lancer l'analyse",
      analysis_button_loading: 'Analyse en cours…',
      exam_section_title: 'Examens enregistrés',
      app_name: 'Careflai',
      raw_image_label: 'Image brute',
      results_label: 'Résultats de validation',
    },
    ai: {
      enabled: true,
      premium: true,   // default: AI locked — manual workflow is the base
      show_interpretation: true,
      show_profile_analysis: true,
      show_scoring: false,
      include_ai_in_export: true,
    },
    collaboration: { enabled: true },
    comments: { enabled: true, required_before_validation: false },
    export: {
      include_raw_images: true,
      include_detected_bands: true,
      include_comments: true,
      include_operator: true,
      include_ai_interpretation: false, // off by default (no AI in base plan)
    },
    operator: { enabled: true },
  },
}

// ─── CHU-specific overrides ────────────────────────────────────────────────
// Keys are matched case-insensitively with substring support (see resolveCHUConfig).
// Use lowercase keys for matching.

const CHU_OVERRIDES: Record<string, Partial<CHUFeatures>> = {
  // Nancy — AI module unlocked (active partnership)
  'chu nancy': {
    ui_labels: {
      ...DEFAULT_CHU_CONFIG.features.ui_labels,
      analysis_button: "Lancer l'analyse",
      exam_section_title: "Examens d'immunologie",
      app_name: 'Careflai — CHU Nancy',
    },
    ai: {
      ...DEFAULT_CHU_CONFIG.features.ai,
      enabled: true,
      premium: true,   // set to true so upsell section is visible during testing
      show_interpretation: true,
      include_ai_in_export: true,
    },
    comments: { enabled: true, required_before_validation: false },
    collaboration: { enabled: true },
    export: {
      ...DEFAULT_CHU_CONFIG.features.export,
      include_ai_interpretation: true,
    },
  },

  // Amiens — AI module unlocked
  'chu amiens': {
    ui_labels: {
      ...DEFAULT_CHU_CONFIG.features.ui_labels,
      analysis_button: "Lancer l'analyse",
      app_name: 'Careflai — CHU Amiens',
    },
    ai: {
      ...DEFAULT_CHU_CONFIG.features.ai,
      enabled: true,
      premium: true,
      show_interpretation: true,
      include_ai_in_export: true,
    },
    export: {
      ...DEFAULT_CHU_CONFIG.features.export,
      include_ai_interpretation: true,
    },
  },

  // Grenoble
  'chu grenoble': {
    ui_labels: {
      ...DEFAULT_CHU_CONFIG.features.ui_labels,
      analysis_button: "Lancer l'analyse",
      app_name: 'Careflai — CHU Grenoble',
    },
    ai: {
      ...DEFAULT_CHU_CONFIG.features.ai,
      enabled: true,
      premium: true,
    },
  },

  // AP-HP
  'ap-hp': {
    ui_labels: {
      ...DEFAULT_CHU_CONFIG.features.ui_labels,
      analysis_button: "Lancer l'analyse",
      app_name: 'Careflai — AP-HP',
    },
    ai: {
      ...DEFAULT_CHU_CONFIG.features.ai,
      enabled: true,
      premium: true,
    },
  },

  // Lyon
  'chu lyon': {
    ui_labels: {
      ...DEFAULT_CHU_CONFIG.features.ui_labels,
      analysis_button: "Lancer l'analyse",
      app_name: 'Careflai — HCL',
    },
    ai: {
      ...DEFAULT_CHU_CONFIG.features.ai,
      enabled: true,
      premium: true,
    },
  },

  // Bordeaux — exploratoire (bandelettes Dynablot, profil de détection encore en rectangle,
  // pas de modèle YOLO dédié pour l'instant). AI verrouillé (premium) tant que la détection
  // n'est pas fiabilisée sur leur géométrie.
  'chu bordeaux': {
    ui_labels: {
      ...DEFAULT_CHU_CONFIG.features.ui_labels,
      analysis_button: "Lancer l'analyse",
      app_name: 'Careflai — CHU Bordeaux',
    },
    ai: {
      ...DEFAULT_CHU_CONFIG.features.ai,
      enabled: true,
      premium: true,
    },
  },
}

// List of selectable CHU configs (for the admin test selector). "default" first.
export const AVAILABLE_CHU_CONFIGS: Array<{ key: string; label: string }> = [
  { key: 'default', label: DEFAULT_CHU_CONFIG.features.ui_labels.app_name },
  ...Object.keys(CHU_OVERRIDES).map((k) => ({
    key: k,
    label: CHU_OVERRIDES[k].ui_labels?.app_name ?? k,
  })),
]

// ─── Alias table ──────────────────────────────────────────────────────────
// Maps normalized official names (from Organizations DB) → canonical CHU_OVERRIDES key.
// Needed because companyName can be set to org.officialName which may differ from
// the short key (e.g. "CHRU Nancy" → "chu nancy", "Hospices Civils de Lyon" → "chu lyon").
// Add normalizedName values from the Organizations table here.

const CHU_ALIASES: Record<string, string> = {
  // Nancy
  'chru nancy': 'chu nancy',
  'chu de nancy': 'chu nancy',
  'centre hospitalier regional universitaire de nancy': 'chu nancy',
  // Amiens
  'chu amiens picardie': 'chu amiens',
  'centre hospitalier universitaire amiens picardie': 'chu amiens',
  // Lyon
  'hospices civils de lyon': 'chu lyon',
  'hcl': 'chu lyon',
  // Grenoble
  'chu grenoble alpes': 'chu grenoble',
  'centre hospitalier universitaire grenoble alpes': 'chu grenoble',
  'chru de grenoble': 'chu grenoble',
  // AP-HP
  'ap hp': 'ap-hp',
  'aphp': 'ap-hp',
  'assistance publique hopitaux de paris': 'ap-hp',
  'ap hp assistance publique hopitaux de paris': 'ap-hp',
  // Strasbourg
  'chru de strasbourg': 'chu strasbourg',
  'chru strasbourg': 'chu strasbourg',
  // Tours
  'chru de tours': 'chu tours',
  'chru tours': 'chu tours',
  // Bordeaux
  'chu de bordeaux': 'chu bordeaux',
}

/**
 * Resolves the CHU config for a given companyName.
 *
 * Resolution order:
 * 1. Exact match in CHU_OVERRIDES (normalized)
 * 2. Alias table lookup (handles official org names like "CHRU Nancy", "Hospices Civils de Lyon")
 * 3. Substring match in CHU_OVERRIDES keys
 * 4. Falls back to DEFAULT_CHU_CONFIG if nothing matches.
 */
export function resolveCHUConfig(companyName?: string | null): CHUConfig {
  if (!companyName) return DEFAULT_CHU_CONFIG

  // Normalize: lowercase, remove accents, replace dashes/underscores/em-dashes with spaces
  const needle = companyName
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[—–\-_]/g, ' ')                          // em dash, en dash, hyphen → space
    .replace(/\s+/g, ' ')
    .trim()

  // 1. Exact match in overrides
  if (CHU_OVERRIDES[needle]) {
    return buildConfig(companyName, CHU_OVERRIDES[needle])
  }

  // 2. Alias table — handles official organization names from DB
  const aliasKey = CHU_ALIASES[needle]
  if (aliasKey && CHU_OVERRIDES[aliasKey]) {
    return buildConfig(companyName, CHU_OVERRIDES[aliasKey])
  }

  // 3. Substring match — needle contains a key or a key contains the needle
  const matchKey = Object.keys(CHU_OVERRIDES).find(
    (key) => needle.includes(key) || key.includes(needle)
  )
  if (matchKey) {
    return buildConfig(companyName, CHU_OVERRIDES[matchKey])
  }

  // 4. No match — use default
  return { ...DEFAULT_CHU_CONFIG, id: companyName, name: companyName }
}

function buildConfig(companyName: string, override: Partial<CHUFeatures>): CHUConfig {
  return {
    id: companyName,
    name: companyName,
    features: {
      ui_labels: override.ui_labels ?? DEFAULT_CHU_CONFIG.features.ui_labels,
      ai:            override.ai            ?? DEFAULT_CHU_CONFIG.features.ai,
      collaboration: override.collaboration ?? DEFAULT_CHU_CONFIG.features.collaboration,
      comments:      override.comments      ?? DEFAULT_CHU_CONFIG.features.comments,
      export:        override.export        ?? DEFAULT_CHU_CONFIG.features.export,
      operator:      override.operator      ?? DEFAULT_CHU_CONFIG.features.operator,
    },
  }
}

/** Returns true when the AI interpretation features are accessible (enabled AND not premium-locked). */
export function isAIAccessible(config: CHUConfig): boolean {
  return config.features.ai.enabled && !config.features.ai.premium
}
